// ============================================================
// KioskPage — 店內打卡站（一樓 Mac mini / iPad 整天開著）
// ------------------------------------------------------------
// 員工點自己的名字 → 選動作 → 輸入 PIN → 完成，3 秒後自動回到名單。
// 裝置本身在店裡，所以完全不需要 GPS——這就是它存在的理由：
// 手機在室內定位誤差動輒數百公尺，員工人在店裡卻常被判「不在範圍內」。
//
// 這台是共用裝置：
// - 用獨立的「打卡站憑證」（kioskApi），只能列名單與打卡，碰不到任何人的資料
// - 掛載時把裝置上殘留的員工登入清掉
// - 選了名字沒輸 PIN 就走開，30 秒自動回名單
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { kioskApi } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { formatTaipeiTime } from '../lib/datetime';
import { BrandMark } from './BrandMark';

type State = 'NOT_STARTED' | 'WORKING' | 'ON_BREAK' | 'RETURNED' | 'FINISHED';
type Action = 'clock-in' | 'clock-out' | 'break-out' | 'break-in';

interface StaffEntry {
  id: string;
  name: string;
  hasPin: boolean;
  state: State;
  clockIn: string | null;
  breakOut: string | null;
  breakIn: string | null;
  clockOut: string | null;
}

const STATE_BADGE: Record<State, { label: string; cls: string }> = {
  NOT_STARTED: { label: '未上班', cls: 'bg-morandi-100 text-morandi-500' },
  WORKING:     { label: '上班中', cls: 'bg-sage-100 text-sage-700' },
  ON_BREAK:    { label: '休息中', cls: 'bg-earth-200 text-earth-700' },
  RETURNED:    { label: '上班中', cls: 'bg-sage-100 text-sage-700' },
  FINISHED:    { label: '已下班', cls: 'bg-morandi-100 text-morandi-400' },
};

const ACTION_LABEL: Record<Action, string> = {
  'clock-in': '上班',
  'clock-out': '下班',
  'break-out': '開始休息',
  'break-in': '結束休息',
};

/** 依今日狀態給主要／次要動作（與手機首頁同樣的習慣：上班中時「下班」為主、「開始休息」為次） */
function actionsFor(state: State): { primary: Action | null; secondary: Action | null } {
  switch (state) {
    case 'NOT_STARTED': return { primary: 'clock-in', secondary: null };
    case 'WORKING':     return { primary: 'clock-out', secondary: 'break-out' };
    case 'ON_BREAK':    return { primary: 'break-in', secondary: null };
    case 'RETURNED':    return { primary: 'clock-out', secondary: null };
    default:            return { primary: null, secondary: null };
  }
}

const IDLE_MS = 30_000;
const DONE_MS = 3_000;
const REFRESH_MS = 60_000;

// ─── 數字鍵盤（啟用碼與 PIN 共用）──────────────────────────
function Keypad({ onDigit, onClear, onBackspace, disabled }: {
  onDigit: (d: string) => void; onClear: () => void; onBackspace: () => void; disabled?: boolean;
}) {
  const key = 'aspect-square bg-white hover:bg-morandi-100 active:scale-95 rounded-2xl text-3xl font-light text-morandi-700 shadow-card transition-all disabled:opacity-50';
  return (
    <div className="grid grid-cols-3 gap-3 w-full">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
        <button key={n} onClick={() => onDigit(String(n))} disabled={disabled} className={key}>{n}</button>
      ))}
      <button onClick={onClear} disabled={disabled}
        className="aspect-square text-base text-morandi-400 hover:text-morandi-600 active:scale-95 disabled:opacity-50">清除</button>
      <button onClick={() => onDigit('0')} disabled={disabled} className={key}>0</button>
      <button onClick={onBackspace} disabled={disabled}
        className="aspect-square text-2xl text-morandi-400 hover:text-morandi-600 active:scale-95 disabled:opacity-50">⌫</button>
    </div>
  );
}

function Dots({ count, total }: { count: number; total: number }) {
  return (
    <div className="flex gap-3 justify-center">
      {Array.from({ length: Math.max(total, count) }).map((_, i) => (
        <div key={i} className={`w-4 h-4 rounded-full transition-colors ${i < count ? 'bg-morandi-700' : 'bg-morandi-200'}`} />
      ))}
    </div>
  );
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
}

// ─── 啟用畫面 ──────────────────────────────────────────────
function ActivateView({ onActivated }: { onActivated: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async (c: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await kioskApi.post<{ token: string }>('/kiosk/activate', { code: c });
      kioskApi.setToken(r.token);
      onActivated();
    } catch (e) {
      setError((e as Error).message);
      setCode('');
    } finally {
      setBusy(false);
    }
  }, [onActivated]);

  useEffect(() => { if (code.length === 6) submit(code); }, [code, submit]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-morandi-50 p-6">
      <div className="w-full max-w-xs flex flex-col items-center gap-5">
        <BrandMark variant="lockup" tone="black" height={88} />
        <div className="text-center">
          <h1 className="text-xl font-bold text-morandi-800">啟用店內打卡站</h1>
          <p className="text-sm text-morandi-500 mt-2 leading-relaxed">
            請老闆在手機的「門市設定 → 店內打卡站」<br />按「產生啟用碼」，在這裡輸入那 6 位數
          </p>
        </div>
        <Dots count={code.length} total={6} />
        {error && <div className="text-sm text-rose-600 text-center">{error}</div>}
        {busy && <div className="text-sm text-morandi-400">驗證中…</div>}
        <Keypad
          disabled={busy}
          onDigit={d => setCode(c => (c.length < 6 ? c + d : c))}
          onClear={() => setCode('')}
          onBackspace={() => setCode(c => c.slice(0, -1))}
        />
      </div>
    </div>
  );
}

// ─── 主畫面 ────────────────────────────────────────────────
export function KioskPage() {
  const now = useClock();
  const [activated, setActivated] = useState(() => !!kioskApi.getToken());
  const [shopName, setShopName] = useState('');
  const [staff, setStaff] = useState<StaffEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selected, setSelected] = useState<StaffEntry | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ name: string; message: string } | null>(null);

  // 共用裝置：不留任何員工或老闆的登入
  useEffect(() => {
    if (localStorage.getItem('ts_token')) useAuthStore.getState().logout();
  }, []);

  const reset = useCallback(() => {
    setSelected(null); setAction(null); setPin(''); setPinError(null); setDone(null);
  }, []);

  const loadStaff = useCallback(async () => {
    if (!kioskApi.getToken()) { setActivated(false); return; }
    try {
      const r = await kioskApi.get<{ tenant: { name: string }; staff: StaffEntry[] }>('/kiosk/staff');
      setShopName(r.tenant.name);
      setStaff(r.staff);
      setLoadError(null);
    } catch (e) {
      // 憑證失效（401）時 kioskApi 會清掉憑證 → 回到啟用畫面
      if (!kioskApi.getToken()) setActivated(false);
      else setLoadError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    if (!activated) return;
    loadStaff();
    const t = setInterval(loadStaff, REFRESH_MS);
    return () => clearInterval(t);
  }, [activated, loadStaff]);

  // 選了人但沒完成 → 30 秒自動回名單（每次按鍵重新計時）
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(idleTimer.current);
    if (selected && !done) idleTimer.current = setTimeout(reset, IDLE_MS);
    return () => clearTimeout(idleTimer.current);
  }, [selected, action, pin, done, reset]);

  // 完成畫面 3 秒後回名單
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => { reset(); loadStaff(); }, DONE_MS);
    return () => clearTimeout(t);
  }, [done, reset, loadStaff]);

  const punch = useCallback(async (p: string) => {
    if (!selected || !action) return;
    setSubmitting(true);
    setPinError(null);
    try {
      const r = await kioskApi.post<{ message: string }>('/kiosk/punch', { userId: selected.id, pin: p, action });
      setDone({ name: selected.name, message: r.message });
    } catch (e) {
      if (!kioskApi.getToken()) { setActivated(false); reset(); return; }
      setPinError((e as Error).message);
      setPin('');
    } finally {
      setSubmitting(false);
    }
  }, [selected, action, reset]);

  // 滿 4 碼稍等一下自動送出（5–6 碼的人可以繼續按，或按「確認」）
  useEffect(() => {
    if (pin.length < 4 || submitting || !action) return;
    const t = setTimeout(() => punch(pin), pin.length >= 6 ? 0 : 700);
    return () => clearTimeout(t);
  }, [pin, submitting, action, punch]);

  if (!activated) return <ActivateView onActivated={() => setActivated(true)} />;

  const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const dateStr = now.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'long' });

  // ── 完成 ──
  if (done) {
    return (
      <button onClick={() => { reset(); loadStaff(); }}
        className="min-h-screen w-full flex flex-col items-center justify-center bg-sage-50 p-6 gap-4">
        <div className="w-28 h-28 rounded-full bg-sage-500 text-white text-6xl flex items-center justify-center shadow-float">✓</div>
        <div className="text-3xl font-bold text-morandi-800">{done.name}</div>
        <div className="text-xl text-sage-700 text-center">{done.message}</div>
        <div className="text-sm text-morandi-400 mt-4">3 秒後自動回到名單</div>
      </button>
    );
  }

  // ── 選動作／輸入 PIN ──
  if (selected) {
    const { primary, secondary } = actionsFor(selected.state);
    const times: [string, string | null][] = [
      ['上班', selected.clockIn], ['休息', selected.breakOut], ['返回', selected.breakIn], ['下班', selected.clockOut],
    ];
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-morandi-50 p-6">
        <div className="w-full max-w-sm flex flex-col items-center gap-5">
          <button onClick={reset} className="self-start text-base text-morandi-500 hover:text-morandi-700 py-2">‹ 返回名單</button>
          <div className="text-3xl font-bold text-morandi-800">{selected.name}</div>

          <div className="grid grid-cols-4 gap-2 w-full">
            {times.map(([label, t]) => (
              <div key={label} className="bg-white rounded-card shadow-card py-2 text-center">
                <div className="text-xs text-morandi-400">{label}</div>
                <div className="font-mono text-morandi-700">{formatTaipeiTime(t)}</div>
              </div>
            ))}
          </div>

          {!action ? (
            primary ? (
              <div className="w-full flex flex-col gap-3 mt-2">
                <button onClick={() => setAction(primary)}
                  className={`w-full py-8 rounded-2xl text-3xl font-bold text-white shadow-float active:scale-[0.98] transition-all
                    ${primary === 'clock-out' ? 'bg-rose-500 hover:bg-rose-600' : 'bg-sage-500 hover:bg-sage-600'}`}>
                  {ACTION_LABEL[primary]}
                </button>
                {secondary && (
                  <button onClick={() => setAction(secondary)}
                    className="w-full py-5 rounded-2xl text-xl font-medium text-earth-700 bg-earth-100 hover:bg-earth-200 active:scale-[0.98] transition-all">
                    {ACTION_LABEL[secondary]}
                  </button>
                )}
              </div>
            ) : (
              <div className="w-full py-8 rounded-2xl bg-morandi-100 text-center text-xl text-morandi-500">✓ 今日已完成打卡</div>
            )
          ) : (
            <>
              <div className="text-lg text-morandi-600">
                <span className="font-bold text-morandi-800">{ACTION_LABEL[action]}</span>　請輸入 PIN
              </div>
              <Dots count={pin.length} total={4} />
              {pin.length >= 4 && !submitting && (
                <button onClick={() => punch(pin)}
                  className="px-8 py-2 rounded-full bg-sage-500 text-white font-medium hover:bg-sage-600 active:scale-95 transition-all">
                  確認
                </button>
              )}
              {pinError && <div className="text-base text-rose-600 text-center">{pinError}</div>}
              {submitting && <div className="text-base text-morandi-400">打卡中…</div>}
              <Keypad
                disabled={submitting}
                onDigit={d => { setPinError(null); setPin(p => (p.length < 6 ? p + d : p)); }}
                onClear={() => setPin('')}
                onBackspace={() => setPin(p => p.slice(0, -1))}
              />
              <button onClick={() => { setAction(null); setPin(''); setPinError(null); }}
                className="text-sm text-morandi-400 hover:text-morandi-600">選錯了，換一個動作</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── 名單 ──
  return (
    <div className="min-h-screen bg-morandi-50 p-6 md:p-10">
      <div className="max-w-4xl mx-auto">
        <header className="flex items-end justify-between mb-8">
          <div>
            <div className="text-sm text-morandi-400">{shopName} · 店內打卡站</div>
            <div className="text-base text-morandi-500 mt-1">{dateStr}</div>
          </div>
          <div className="text-6xl md:text-7xl font-mono font-light text-morandi-800 tracking-wider">{timeStr}</div>
        </header>

        {loadError && (
          <div className="mb-4 rounded-card bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">
            {loadError}（每分鐘會自動重試）
          </div>
        )}

        <div className="text-lg text-morandi-600 mb-4">請點自己的名字</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {staff.map(s => {
            const badge = STATE_BADGE[s.state];
            const last = s.clockOut ?? s.breakIn ?? s.breakOut ?? s.clockIn;
            return (
              <button key={s.id}
                onClick={() => s.hasPin && setSelected(s)}
                disabled={!s.hasPin}
                className={`text-left rounded-2xl p-5 min-h-[128px] flex flex-col justify-between transition-all
                  ${s.hasPin ? 'bg-white shadow-card hover:shadow-card-lg active:scale-[0.98]' : 'bg-morandi-100 opacity-60 cursor-not-allowed'}`}>
                <div className="text-2xl font-bold text-morandi-800">{s.name}</div>
                <div className="flex items-center justify-between mt-3">
                  {s.hasPin
                    ? <span className={`text-sm px-2.5 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                    : <span className="text-sm text-morandi-500">尚未設定 PIN</span>}
                  {last && <span className="text-sm font-mono text-morandi-400">{formatTaipeiTime(last)}</span>}
                </div>
              </button>
            );
          })}
        </div>
        {staff.length === 0 && !loadError && (
          <div className="text-center text-morandi-400 py-16">載入中…</div>
        )}
      </div>
    </div>
  );
}
